import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchBookPreviewHtml, getAdminBookCollective, listAdminLibrary, listUnfinalizedBooks, purgeUnfinalizedBooks } from '../../../services/adminApi';
import useConfirm from '../shell/useConfirm';
import { adminPath } from '../shell/adminNav';
import { Tag } from '../catalog/ui';
import '../catalog/catalog.css';
import '../orders/orders.css';
import './people.css';

// Livres : où les gens s'arrêtent. Chiffres clés, filtre par état, tableau avec l'avancement (pages remplies / prévues),
// consultation en lecture seule (le même moteur que l'aperçu du client) et nettoyage des brouillons abandonnés.
// Tout est dans l'adresse (?etat=…&q=…&page=…) : lien partageable, retour arrière qui fonctionne.

const ETATS = ['en_cours', 'presque', 'termine', 'commande', 'abandonne'];
const TON_ETAT = { en_cours: '', presque: 'warn', termine: 'ok', commande: 'ok', abandonne: 'mute' };

function Kpi({ label, value, note, tone = '' }) {
  return (
    <div className="adm-kpi">
      <span>{label}</span>
      <b>{value}</b>
      {note && <small className={tone}>{note}</small>}
    </div>
  );
}

export default function BooksSection() {
  const { t, i18n } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const etat = params.get('etat') || '';
  const q = params.get('q') || '';
  const page = Number(params.get('page')) || 1;
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [saisie, setSaisie] = useState(q);
  const [apercu, setApercu] = useState(null); // { book, html, loading }
  const [nettoyage, setNettoyage] = useState(false);
  const [confirmer, confirmation] = useConfirm();
  const derniere = useRef(0);

  const changer = (modifs) => {
    const suivant = new URLSearchParams(params);
    Object.entries(modifs).forEach(([cle, valeur]) => { if (valeur) suivant.set(cle, valeur); else suivant.delete(cle); });
    if (!('page' in modifs)) suivant.delete('page');
    setParams(suivant, { replace: true });
  };

  const charger = useCallback(async () => {
    const numero = ++derniere.current;
    try {
      const r = await listAdminLibrary({ etat, q, page });
      if (numero === derniere.current) { setData(r); setErreur(''); }
    } catch (err) {
      if (numero === derniere.current) setErreur(err?.message || t('books.loadFailed'));
    }
  }, [etat, q, page, t]);
  useEffect(() => { charger(); }, [charger]);

  useEffect(() => { setSaisie(q); }, [q]);
  useEffect(() => {
    if (saisie === q) return undefined;
    const minuteur = setTimeout(() => changer({ q: saisie.trim() }), 300);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saisie]);

  const consulter = async (livre) => {
    setApercu({ book: livre, html: '', loading: true, collectif: null });
    // Le collectif du livre en chiffres (refonte du collectif, phase 5) : facultatif, l'apercu s'affiche sans lui.
    const collectif = livre.collective ? await getAdminBookCollective(livre.id).catch(() => null) : null;
    try {
      setApercu({ book: livre, html: await fetchBookPreviewHtml(livre.id), loading: false, collectif });
    } catch (err) {
      setApercu(null);
      setErreur(err?.message || t('books.previewFailed'));
    }
  };

  const nettoyer = async () => {
    setNettoyage(true);
    setErreur('');
    setMessage('');
    try {
      const prevision = await listUnfinalizedBooks();
      if (prevision.total === 0) { setMessage(t('books.cleanNone')); return; }
      const details = prevision.books.slice(0, 15).map((b) => t('books.cleanLine', { title: b.title || t('books.untitled'), date: new Date(b.createdAt).toLocaleDateString(i18n.language) }));
      if (prevision.total > 15) details.push(t('books.cleanMore', { count: prevision.total - 15 }));
      if (!(await confirmer({
        title: t('books.cleanTitle', { count: prevision.total }),
        message: t('books.cleanBody'),
        details,
        confirmLabel: t('books.cleanYes')
      }))) return;
      await purgeUnfinalizedBooks();
      await charger();
    } catch (err) {
      setErreur(err?.message || t('books.cleanFailed'));
    } finally {
      setNettoyage(false);
    }
  };

  const stats = data && data.stats;
  const premier = data ? (data.page - 1) * data.perPage + 1 : 0;
  const dernier = data ? Math.min(data.page * data.perPage, data.total) : 0;
  const pourcent = (livre) => (livre.pages.total > 0 ? Math.min(100, Math.round((livre.pages.filled / livre.pages.total) * 100)) : 0);

  return (
    <>
      {stats && (
        <div className="adm-kpis">
          <Kpi label={t('books.kpi.started')} value={stats.total} note={t('books.kpi.startedNote')} />
          <Kpi label={t('books.kpi.inProgress')} value={stats.inProgressThisWeek} note={t('books.kpi.inProgressNote', { days: data.thresholds.activeDays })} />
          <Kpi label={t('books.kpi.finished')} value={stats.states.termine} note={t('books.kpi.finishedNote')} />
          <Kpi label={t('books.kpi.ordered')} value={stats.states.commande} note={stats.total ? t('books.kpi.orderedNote', { percent: Math.round((stats.states.commande / stats.total) * 100) }) : ''} />
          <Kpi label={t('books.kpi.abandoned')} value={stats.states.abandonne} note={t('books.kpi.abandonedNote', { days: data.thresholds.abandonedDays })} tone={stats.states.abandonne ? 'is-bad' : ''} />
        </div>
      )}

      <div className="adm-actions">
        <form className="adm-orders-search" role="search" onSubmit={(e) => { e.preventDefault(); changer({ q: saisie.trim() }); }}>
          <label className="adm-sr" htmlFor="adm-books-q">{t('books.searchLabel')}</label>
          <input id="adm-books-q" className="adm-input" type="search" value={saisie} placeholder={t('books.searchPlaceholder')} onChange={(e) => setSaisie(e.target.value)} />
        </form>
        <span className="adm-sp" />
        <button type="button" className="btn btn-outline btn-sm" onClick={nettoyer} disabled={nettoyage}>{nettoyage ? t('books.cleaning') : t('books.clean')}</button>
      </div>

      <div className="adm-chips" role="group" aria-label={t('books.filters')}>
        <button type="button" className="adm-chip" aria-pressed={!etat} onClick={() => changer({ etat: '' })}>{t('books.all')} {stats && <b>{stats.total}</b>}</button>
        {ETATS.map((e) => (
          <button key={e} type="button" className="adm-chip" aria-pressed={etat === e} onClick={() => changer({ etat: etat === e ? '' : e })}>
            {t(`books.states.${e}`)} {stats && <b>{stats.states[e]}</b>}
          </button>
        ))}
      </div>

      {confirmation}
      {message && <p className="adm-note adm-flash" role="status">{message}</p>}
      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      {!data && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}
      {data && data.books.length === 0 && <p className="adm-note">{t('books.empty')}</p>}

      {data && data.books.length > 0 && (
        <div className="adm-tbl">
          <table>
            <thead>
              <tr>
                <th>{t('books.cols.book')}</th><th>{t('books.cols.author')}</th><th>{t('books.cols.format')}</th><th>{t('books.cols.progress')}</th>
                <th>{t('books.cols.state')}</th><th>{t('books.cols.activity')}</th><th><span className="adm-sr">{t('books.cols.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {data.books.map((b) => (
                <tr key={b.id}>
                  <td><b>{b.title || t('books.untitled')}</b>{b.collective && <> <Tag tone="mute">{t('books.collective')}</Tag></>}<small>{b.id.slice(0, 8)}</small></td>
                  <td>
                    {b.owner.anonymous ? t('books.noAccount') : (b.owner.name || b.owner.email || '—')}
                    <small>{b.owner.anonymous ? t('books.noAccountNote') : (b.owner.name ? b.owner.email : '')}</small>
                  </td>
                  <td>{b.format ? t(`orders.formats.${b.format}`, { defaultValue: b.format }) : '—'}</td>
                  <td>
                    <div className="adm-progressrow">
                      <div className="adm-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pourcent(b)} aria-label={t('books.cols.progress')}><i style={{ width: `${pourcent(b)}%` }} /></div>
                      <span>{b.pages.filled}/{b.pages.total}</span>
                    </div>
                    <small>{t('books.content', { photos: b.photos, texts: b.texts })}</small>
                  </td>
                  <td>
                    <Tag tone={TON_ETAT[b.state]}>{t(`books.states.${b.state}`)}</Tag>
                    {b.orders.length > 0 && (
                      <small>{b.orders.map((o, i) => (
                        <React.Fragment key={o.id}>{i > 0 && ', '}<Link to={`${adminPath('orders')}?commande=${o.id}`}>{o.number || o.id.slice(0, 8)}</Link></React.Fragment>
                      ))}</small>
                    )}
                  </td>
                  <td>{new Date(b.lastActivityAt).toLocaleDateString(i18n.language)}</td>
                  <td><button type="button" className="btn btn-outline btn-sm" onClick={() => consulter(b)}>{t('books.view')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > data.perPage && (
        <nav className="adm-pager" aria-label={t('books.pagination')}>
          <span className="adm-note">{t('orders.range', { from: premier, to: dernier, total: data.total })}</span>
          <span className="adm-sp" />
          <button type="button" className="btn btn-outline btn-sm" disabled={data.page <= 1} onClick={() => changer({ page: String(data.page - 1) })}>{t('orders.previous')}</button>
          <button type="button" className="btn btn-outline btn-sm" disabled={dernier >= data.total} onClick={() => changer({ page: String(data.page + 1) })}>{t('orders.next')}</button>
        </nav>
      )}
      <p className="adm-note">{t('books.rules', { days: data ? data.thresholds.abandonedDays : 14, percent: data ? data.thresholds.almostPercent : 80 })}</p>

      {apercu && (
        <div className="adm-preview" onMouseDown={(e) => { if (e.target === e.currentTarget) setApercu(null); }} onKeyDown={(e) => { if (e.key === 'Escape') setApercu(null); }}>
          <div className="adm-preview-panel" role="dialog" aria-modal="true" aria-label={apercu.book.title || t('books.untitled')}>
            <header>
              <div>
                <b>{apercu.book.title || t('books.untitled')}</b>
                <small>{apercu.book.owner.anonymous ? t('books.noAccount') : (apercu.book.owner.email || '—')}</small>
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setApercu(null)} autoFocus>{t('catalog.common.close')}</button>
            </header>
            {apercu.collectif && (
              <div className="adm-collectif" aria-label={t('books.collectiveTitle')}>
                <b>{apercu.collectif.active ? t('books.collectiveActive') : t('books.collectiveInactive')}</b>
                {apercu.collectif.active && (
                  <span>
                    {t('books.collectiveGuests', { count: apercu.collectif.guests })}
                    {' · '}{['invited', 'opened', 'started', 'completed'].map((s) => `${apercu.collectif.statuses[s] || 0} ${t(`books.collectiveStatus.${s}`)}`).join(' · ')}
                    {' · '}{t('books.collectiveReceived', { photos: apercu.collectif.photos, words: apercu.collectif.words })}
                    {apercu.collectif.deadline ? ` · ${t('books.collectiveDeadline', { date: new Date(`${apercu.collectif.deadline}T12:00:00`).toLocaleDateString(i18n.language) })}` : ''}
                    {apercu.collectif.organizerName ? ` · ${t('books.collectiveBy', { name: apercu.collectif.organizerName })}` : ''}
                  </span>
                )}
              </div>
            )}
            {apercu.loading
              ? <p className="adm-state" role="status">{t('books.rendering')}</p>
              // srcDoc et non src : le HTML est récupéré avec l'en-tête d'authentification, le jeton ne passe jamais par une URL.
              : <iframe title={t('books.previewTitle')} srcDoc={apercu.html} />}
          </div>
        </div>
      )}
    </>
  );
}
